import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "overlay",
    "thumbnail",
    "filename",
    "filesize",
    "caption",
    "captionWrapper",
    "error",
    "form",
    "formFile",
    "thread"
  ]

  static values = {
    rules: Object,
    labels: Object
  }

  connect() {
    this.objectUrl = null
  }

  disconnect() {
    this.revokeObjectUrl()
  }

  openPicker() {
    this.clearError()
    this.inputTarget.click()
  }

  fileSelected() {
    const file = this.inputTarget.files?.[0]
    this.inputTarget.value = ""

    if (!file) return

    this.handleIncomingFile(file)
  }

  paste(event) {
    const item = Array.from(event.clipboardData?.items || [])
      .find((entry) => entry.kind === "file" && entry.type.startsWith("image/"))

    if (!item) return

    event.preventDefault()
    this.handleIncomingFile(item.getAsFile())
  }

  dragOver(event) {
    event.preventDefault()
    this.showDropTarget()
  }

  dragLeave(event) {
    if (this.hasThreadTarget && this.threadTarget.contains(event.relatedTarget)) return

    this.clearDropTarget()
  }

  drop(event) {
    event.preventDefault()
    this.clearDropTarget()

    const file = event.dataTransfer?.files?.[0]
    if (!file) return

    this.handleIncomingFile(file)
  }

  handleIncomingFile(file) {
    const validation = this.validateFile(file)
    if (!validation.valid) {
      this.showError(validation.message)
      return
    }

    this.openPreview(file, validation.rule)
  }

  cancel(event) {
    event?.preventDefault()
    this.closePreview()
  }

  validateFile(file) {
    const rule = this.rulesValue[file.type]
    if (!rule) {
      return { valid: false, message: this.labelsValue.unsupportedType }
    }

    if (file.size > rule.max_bytes) {
      return { valid: false, message: this.labelsValue.fileTooLarge }
    }

    return { valid: true, rule }
  }

  validateCaption(file, rule) {
    const caption = this.hasCaptionTarget ? this.captionTarget.value : ""
    if (caption.length > 1024) {
      return { valid: false, message: this.labelsValue.captionTooLong }
    }

    if (caption.length > 0 && !rule.captionable) {
      return { valid: false, message: this.labelsValue.captionNotAllowedOnAudio }
    }

    return { valid: true }
  }

  openPreview(file, rule) {
    this.clearError()
    this.revokeObjectUrl()
    this.assignFileToForm(file)

    this.filenameTarget.textContent = file.name
    this.filesizeTarget.textContent = this.formatFileSize(file.size)

    if (rule.captionable) {
      this.captionWrapperTarget.classList.remove("hidden")
      this.captionTarget.value = ""
    } else {
      this.captionWrapperTarget.classList.add("hidden")
      this.captionTarget.value = ""
    }

    this.renderThumbnail(file)
    this.overlayTarget.classList.remove("hidden")
  }

  closePreview() {
    this.overlayTarget.classList.add("hidden")
    this.clearFormFile()
    this.inputTarget.value = ""
    this.revokeObjectUrl()
    this.clearThumbnail()
    this.clearError()

    if (this.hasCaptionTarget) {
      this.captionTarget.value = ""
    }
  }

  formSubmit(event) {
    const file = this.formFileTarget.files?.[0]
    if (!file) {
      event.preventDefault()
      return
    }

    const fileValidation = this.validateFile(file)
    if (!fileValidation.valid) {
      event.preventDefault()
      this.showError(fileValidation.message)
      return
    }

    const captionValidation = this.validateCaption(file, fileValidation.rule)
    if (!captionValidation.valid) {
      event.preventDefault()
      this.showError(captionValidation.message)
    }
  }

  assignFileToForm(file) {
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.formFileTarget.files = transfer.files
  }

  clearFormFile() {
    this.formFileTarget.value = ""
  }

  renderThumbnail(file) {
    if (!file.type.startsWith("image/")) {
      this.thumbnailTarget.classList.add("hidden")
      this.thumbnailTarget.innerHTML = ""
      return
    }

    this.objectUrl = URL.createObjectURL(file)
    this.thumbnailTarget.innerHTML = ""
    const image = document.createElement("img")
    image.src = this.objectUrl
    image.alt = file.name
    image.className = "h-full w-full object-contain"
    this.thumbnailTarget.appendChild(image)
    this.thumbnailTarget.classList.remove("hidden")
  }

  clearThumbnail() {
    this.thumbnailTarget.innerHTML = ""
    this.thumbnailTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  showDropTarget() {
    if (!this.hasThreadTarget) return

    this.threadTarget.classList.add("ring-2", "ring-inset", "ring-[#00a884]/60")
  }

  clearDropTarget() {
    if (!this.hasThreadTarget) return

    this.threadTarget.classList.remove("ring-2", "ring-inset", "ring-[#00a884]/60")
  }

  revokeObjectUrl() {
    if (!this.objectUrl) return

    URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 B"

    const units = [ "B", "KB", "MB", "GB" ]
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
    const value = bytes / (1024 ** exponent)
    const formatted = value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1)

    return `${formatted} ${units[exponent]}`
  }
}
